import { useState, useEffect } from "react";
import { ClipboardList, Eye, Plus, Check, X, ChevronDown, ChevronRight, Trash2, Download, FileText, AlertCircle, Lock } from "lucide-react";
import { supabase } from "./supabase";

// ========= Design tokens =========
// Palette: deep ink + warm off-white + single saturated accent (a muted oxblood).
// Typography: a display serif (Fraunces) for headings, a technical mono (JetBrains Mono) for labels, humanist sans (Inter Tight) for body.
// Aesthetic target: a well-engineered workshop form — precise, adult, slightly editorial.

const VEHICLES = ["Sedan / Coupe", "Mid-Size SUV / Crossover", "Large SUV / Truck", "XL / Exotic"];
const INCLUSIONS = [
  "Glass/windows coating",
  "Wheel faces coating",
  "Wheels off — barrels + calipers",
  "Interior deep clean",
  "Interior fabric/leather coating",
  "Engine bay detail",
];
// Paint correction is handled separately with its own options (single-stage / two-stage)

// Add-ons now specify an input "type" so each behaves appropriately:
//   per_vehicle  → price across all 6 vehicle categories
//   flat         → single price, no vehicle variant
//   per_unit     → single price + a unit label (e.g. "per seat", "per headlight")
// Optional `note` is a short explanatory prompt under the label.
// Optional `extraField` adds an extra text input (used for warranty on roof coating).
const ADDONS = [
  { name: "Interior Mini Detail", type: "per_vehicle" },
  { name: "Interior Deep Clean", type: "per_vehicle" },
  { name: "Single-Stage Paint Correction", type: "per_vehicle" },
  { name: "Two-Stage Paint Correction", type: "per_vehicle" },
  { name: "Wheel Face Coating (4 wheels)", type: "flat", note: "Price doesn't usually vary much by wheel size." },
  { name: "Wheels Off Detail (clean only)", type: "per_vehicle", note: "Wheels removed, barrels & calipers cleaned — no coating." },
  { name: "Wheels Off + Coating (faces, barrels & calipers)", type: "per_vehicle", note: "Full wheels-off service including ceramic coating of all surfaces." },
  { name: "Glass/Window Coating", type: "per_vehicle" },
  { name: "Engine Bay Detail", type: "flat", note: "Typically a flat price — doesn't vary much by vehicle size." },
  { name: "Convertible Top Clean + Fabric Coat", type: "flat", note: "Skip if you don't offer this. Not applicable to transit vans.", extraField: { key: "warranty", label: "How long does the fabric sealant/coating last?", placeholder: "e.g. 2 years" } },
  { name: "Leather Protection/Coating", type: "per_unit", unitLabel: "per seat", note: "Most shops charge per seat rather than by vehicle size." },
  { name: "Headlight Restoration", type: "per_unit", unitLabel: "per headlight", note: "Price per headlight — typically doesn't vary by vehicle size." },
];

const blankLevel = () => ({
  description: "",
  warranty: "",
  paintCorrection: "", // "none" | "single" | "two-stage" | "upgrade"
  inclusions: INCLUSIONS.reduce((acc, i) => ({ ...acc, [i]: "no" }), {}),
  prices: VEHICLES.reduce((acc, v) => ({ ...acc, [v]: "" }), {}),
});

// Each addon stores shape appropriate to its type.
// per_vehicle: { prices: { City Car: "", ... } }
// flat:        { price: "", warranty?: "" }
// per_unit:    { price: "" }  (unit label comes from the addon definition)
const blankAddonValue = (addon) => {
  if (addon.type === "per_vehicle") {
    return { prices: VEHICLES.reduce((acc, v) => ({ ...acc, [v]: "" }), {}) };
  }
  if (addon.type === "flat") {
    const base = { price: "" };
    if (addon.extraField) base[addon.extraField.key] = "";
    return base;
  }
  if (addon.type === "per_unit") {
    return { price: "" };
  }
  return {};
};

const blankPricingTier = () => ({ tier: "", price: "", notes: "" });

const blankSubmission = () => ({
  business: { name: "", contact: "", email: "", phone: "" },
  levels: {
    good: blankLevel(),
    better: blankLevel(),
    best: blankLevel(),
  },
  addons: ADDONS.reduce((acc, a) => ({ ...acc, [a.name]: blankAddonValue(a) }), {}),
  bonusA: "", // glass/windows + wheel face coating free
  bonusB: "", // interior refresh + engine bay
  bonusAlternative: "",
  stoneChip: { offers: "", description: "" },
  tinting: { offers: "", brand: "", tiers: [blankPricingTier(), blankPricingTier(), blankPricingTier(), blankPricingTier()] },
  ppf: { offers: "", brand: "", tiers: [blankPricingTier(), blankPricingTier(), blankPricingTier(), blankPricingTier()] },
  other: { tiers: [blankPricingTier(), blankPricingTier(), blankPricingTier()] },
  approvals: { accurate: "" },
});

export default function App() {
  const [view, setView] = useState("form"); // "form" | "submissions"
  const [form, setForm] = useState(blankSubmission());
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const ADMIN_PASSWORD = "zenith100";

  // Load submissions on mount
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('intake_us')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        if (data) {
          const loaded = data.map(row => ({
            id: row.id,
            data: typeof row.data === 'string' ? JSON.parse(row.data) : row.data
          }));
          setSubmissions(loaded);
        }
      } catch (e) {
        console.error("Failed to load submissions from Supabase", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async () => {
    if (!form.business.name.trim()) {
      setToast({ type: "error", message: "Please enter a business name before submitting." });
      setTimeout(() => setToast(null), 4000);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    const submittedAt = Date.now();
    const payload = { ...form, submittedAt };
    
    try {
      const { data, error } = await supabase
        .from('intake_us')
        .insert([{ 
          business_name: form.business.name, 
          data: payload 
        }])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const newSub = {
          id: data[0].id,
          data: payload
        };
        setSubmissions(prev => [newSub, ...prev]);
      }
      
      setForm(blankSubmission());
      setJustSubmitted(true);
      setTimeout(() => setJustSubmitted(false), 5000);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error("Supabase Submit error:", e);
      setToast({ type: "error", message: "Submission failed — " + (e?.message || "please try again") });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteSubmission = async (id) => {
    setPendingDelete(id);
  };

  const confirmDelete = async () => {
    const id = pendingDelete;
    setPendingDelete(null);
    if (!id) return;
    try {
      const { error } = await supabase
        .from('intake_us')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setSubmissions(prev => prev.filter(s => s.id !== id));
      if (openSub === id) setOpenSub(null);
      setToast({ type: "success", message: "Submission deleted." });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      console.error("Supabase Delete error:", e);
      setToast({ type: "error", message: "Delete failed — " + (e?.message || "unknown error") });
      setTimeout(() => setToast(null), 5000);
    }
  };

  const exportJSON = (sub) => {
    const blob = new Blob([JSON.stringify(sub.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${sub.data.business.name || "submission"}-${new Date(sub.data.submittedAt).toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen" style={{
      background: "#F4F6FA",
      color: "#0F2A4A",
      fontFamily: '"Inter Tight", system-ui, sans-serif',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        body { background: #F4F6FA; }
        .display { font-family: "Fraunces", Georgia, serif; font-optical-sizing: auto; letter-spacing: -0.02em; }
        .mono { font-family: "JetBrains Mono", ui-monospace, monospace; }
        input, textarea, select { font-family: inherit; }
        .field {
          width: 100%;
          background: #FFFFFF;
          border: 1px solid #B8C5D4;
          border-radius: 4px;
          padding: 9px 12px;
          font-size: 14px;
          color: #0F2A4A;
          transition: all 0.15s;
          outline: none;
        }
        .field:focus {
          border-color: #4A90D9;
          box-shadow: 0 0 0 3px rgba(74, 144, 217, 0.15);
          background: #FFFFFF;
        }
        .field-sm { padding: 6px 10px; font-size: 13px; }
        .section-card {
          background: #FFFFFF;
          border: 1px solid #D5DEE8;
          border-radius: 6px;
          padding: 32px;
          margin-bottom: 28px;
        }
        .subheading {
          font-family: "JetBrains Mono", monospace;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #C9A86A;
        }
        .label {
          font-family: "JetBrains Mono", monospace;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #4A5870;
          margin-bottom: 6px;
          display: block;
        }
        .level-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 10px;
          border-radius: 999px;
          font-family: "JetBrains Mono", monospace;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }
        .btn-primary {
          background: #0F2A4A;
          color: #F4F6FA;
          border: 1px solid #0F2A4A;
          padding: 14px 28px;
          border-radius: 4px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }
        .btn-primary:hover:not(:disabled) { background: #1A3C66; border-color: #C9A86A; box-shadow: 0 0 0 2px rgba(201, 168, 106, 0.3); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-ghost {
          background: transparent;
          border: 1px solid #B8C5D4;
          color: #0F2A4A;
          padding: 8px 14px;
          border-radius: 4px;
          font-weight: 500;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-ghost:hover { background: #E8F0FA; border-color: #C9A86A; }
        .nav-btn {
          background: transparent;
          border: none;
          padding: 8px 16px;
          font-family: "JetBrains Mono", monospace;
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #4A5870;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          transition: all 0.15s;
        }
        .nav-btn.active { color: #0F2A4A; border-bottom-color: #4A90D9; }
        .nav-btn:hover { color: #0F2A4A; }
        .radio-group { display: flex; gap: 8px; flex-wrap: wrap; }
        .radio-opt {
          padding: 6px 14px;
          border: 1px solid #B8C5D4;
          border-radius: 4px;
          background: #FFFFFF;
          font-size: 13px;
          cursor: pointer;
          font-family: "JetBrains Mono", monospace;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #4A5870;
          transition: all 0.15s;
        }
        .radio-opt:hover { border-color: #C9A86A; color: #0F2A4A; }
        .radio-opt.active { background: #0F2A4A; color: #F4F6FA; border-color: #0F2A4A; }
        .radio-opt.active.yes { background: #2F5D3A; border-color: #2F5D3A; }
        .radio-opt.active.no { background: #B84237; border-color: #B84237; }
        .pricing-grid {
          display: grid;
          grid-template-columns: 1.6fr repeat(6, 1fr);
          gap: 8px;
          align-items: center;
        }
        .pricing-grid-header {
          font-family: "JetBrains Mono", monospace;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #4A5870;
          text-align: center;
          padding-bottom: 6px;
        }
        .pricing-grid-label {
          font-size: 13px;
          font-weight: 500;
          color: #0F2A4A;
        }
        .rule {
          height: 1px;
          background: #D5DEE8;
          margin: 20px 0;
        }
        .divider-heavy {
          height: 3px;
          background: #0F2A4A;
          margin: 8px 0 16px;
          width: 40px;
        }
        .callout {
          background: #E8F0FA;
          border-left: 3px solid #C9A86A;
          padding: 16px 20px;
          border-radius: 0 4px 4px 0;
          font-size: 14px;
          line-height: 1.6;
        }
        .sub-card {
          background: #FFFFFF;
          border: 1px solid #D5DEE8;
          border-radius: 6px;
          overflow: hidden;
        }
        .sub-header {
          padding: 16px 20px;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: background 0.15s;
        }
        .sub-header:hover { background: #F4F6FA; }
        .sub-body { padding: 24px; border-top: 1px solid #D5DEE8; background: #F4F6FA; }
        .info-row {
          display: grid;
          grid-template-columns: 160px 1fr;
          gap: 16px;
          padding: 8px 0;
          border-bottom: 1px dashed #D5DEE8;
          font-size: 13px;
        }
        .info-row:last-child { border-bottom: none; }
        .info-label {
          font-family: "JetBrains Mono", monospace;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #4A5870;
        }
        .info-value { color: #0F2A4A; }
        .info-empty { color: #9AA8B8; font-style: italic; }

        /* Mobile-friendly tap targets: min 40px height, generous padding */
        .radio-opt { min-height: 34px; display: inline-flex; align-items: center; }

        /* Prevent iOS zoom-on-focus: input font-size must be >= 16px */
        input.field, textarea.field, select.field { font-size: 16px; }
        .field-sm { font-size: 14px; }

        @media (max-width: 720px) {
          .pricing-grid { grid-template-columns: 1fr; }
          .pricing-grid-header { display: none; }
          .pricing-grid-label { margin-top: 12px; font-weight: 600; }
          .section-card { padding: 18px 14px; border-radius: 4px; }

          /* Generic grid collapse */
          .stack-on-mobile { grid-template-columns: 1fr !important; }

          /* Tiers editor: stack each row's 4 inputs vertically */
          .tiers-header { display: none !important; }
          .tiers-row { grid-template-columns: 1fr !important; gap: 6px !important; padding: 10px; background: #F4F6FA; border-radius: 4px; margin-bottom: 8px !important; }
          .tiers-row > button { justify-self: end; }
          .mobile-only-label { display: block !important; font-family: "JetBrains Mono", monospace; font-size: 9px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #4A5870; margin-bottom: 3px; }

          /* Hero strategy callout: stack 3 columns to 1 */
          .strategy-cols { grid-template-columns: 1fr !important; gap: 16px !important; }

          /* Tighter container padding on mobile */
          .main-container { padding: 24px 14px 60px !important; }
          .header-inner { padding: 14px 14px !important; gap: 10px !important; }

          /* Submit bar: full-width button, stack the note above */
          .submit-bar { flex-direction: column; align-items: stretch !important; }
          .submit-btn { width: 100%; justify-content: center; padding: 16px 20px !important; font-size: 15px !important; }

          /* Approval rows: stack label above buttons so nothing gets squeezed */
          .approval-row { grid-template-columns: 1fr !important; align-items: flex-start !important; }

          /* Inclusion cards: give the yes/no/upgrade buttons room to breathe */
          .inclusion-row { flex-wrap: wrap; }
          .inclusion-row > span { flex-basis: 100%; margin-bottom: 2px; }

          /* Level prices: 2 cols on mobile instead of 6 */
          .level-prices-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* Per-vehicle addon grid: 2 cols on mobile */
          .per-vehicle-grid { grid-template-columns: repeat(2, 1fr) !important; }

          /* Sub-card header */
          .sub-header { flex-wrap: wrap; gap: 10px; }

          /* Submission detail info rows: stack label above value */
          .info-row { grid-template-columns: 1fr !important; gap: 2px !important; padding: 8px 0 !important; }

          /* Nav: compact, icon-only feel */
          .nav-btn { padding: 8px 10px; font-size: 10px; }
          .header-brand-sub { display: none; }

          /* Hero headline scales down */
          .hero-headline { font-size: 34px !important; line-height: 1.05 !important; }
          .page-headline { font-size: 36px !important; }
        }

        @media (max-width: 420px) {
          .section-card { padding: 16px 12px; }
          .hero-headline { font-size: 28px !important; }
          .page-headline { font-size: 30px !important; }
          /* Level prices: single column on very narrow */
          .level-prices-grid { grid-template-columns: 1fr !important; }
          .per-vehicle-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* Header bar */}
      <header style={{ borderBottom: "1px solid #D5DEE8", background: "#FFFFFF" }}>
        <div className="header-inner" style={{ maxWidth: 1100, margin: "0 auto", padding: "18px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 36, height: 36, background: "#0F2A4A", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ClipboardList size={18} color="#F4F6FA" />
            </div>
            <div>
              <div className="display" style={{ fontSize: 20, fontWeight: 600, lineHeight: 1 }}>Studio Intake</div>
              <div className="mono header-brand-sub" style={{ fontSize: 10, letterSpacing: "0.15em", color: "#4A5870", marginTop: 3, textTransform: "uppercase" }}>Client Pricing & Packages</div>
            </div>
          </div>
          <nav style={{ display: "flex", gap: 4 }}>
            <button className={`nav-btn ${view === "form" ? "active" : ""}`} onClick={() => setView("form")}>
              <Plus size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />New Submission
            </button>
            <button className={`nav-btn ${view === "submissions" ? "active" : ""}`} onClick={() => setView("submissions")}>
              {unlocked ? (
                <><Eye size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />Submissions ({submissions.length})</>
              ) : (
                <><Lock size={12} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />Submissions</>
              )}
            </button>
          </nav>
        </div>
      </header>

      <main className="main-container" style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 28px 80px" }}>
        {view === "form" ? (
          <FormView
            form={form}
            setForm={setForm}
            onSubmit={handleSubmit}
            submitting={submitting}
            justSubmitted={justSubmitted}
          />
        ) : unlocked ? (
          <SubmissionsView
            submissions={submissions}
            loading={loading}
            openSub={openSub}
            setOpenSub={setOpenSub}
            onDelete={deleteSubmission}
            onExport={exportJSON}
            onLock={() => { setUnlocked(false); setPwInput(""); }}
          />
        ) : (
          <PasswordGate
            pwInput={pwInput}
            setPwInput={setPwInput}
            pwError={pwError}
            onSubmit={() => {
              if (pwInput.trim().toLowerCase() === ADMIN_PASSWORD) {
                setUnlocked(true);
                setPwError(false);
                setPwInput("");
              } else {
                setPwError(true);
              }
            }}
          />
        )}
      </main>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1000,
          background: toast.type === "error" ? "#B84237" : "#2F5D3A",
          color: "#F4F6FA", padding: "14px 20px", borderRadius: 6,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)", maxWidth: 400,
          display: "flex", alignItems: "center", gap: 10, fontSize: 14,
          fontFamily: '"Inter Tight", system-ui, sans-serif',
        }}>
          {toast.type === "error" ? <AlertCircle size={16} /> : <Check size={16} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Delete confirmation modal */}
      {pendingDelete && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 999,
          background: "rgba(20, 18, 16, 0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20, fontFamily: '"Inter Tight", system-ui, sans-serif',
        }} onClick={() => setPendingDelete(null)}>
          <div style={{
            background: "#FFFFFF", borderRadius: 8, padding: 32, maxWidth: 420,
            width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#FBEAE7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Trash2 size={18} color="#B84237" />
              </div>
              <h3 style={{ fontFamily: '"Fraunces", Georgia, serif', fontSize: 22, fontWeight: 500, margin: 0, letterSpacing: "-0.02em" }}>
                Delete this submission?
              </h3>
            </div>
            <p style={{ color: "#4A5870", fontSize: 14, lineHeight: 1.5, margin: "0 0 24px" }}>
              This will permanently remove the submission. This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setPendingDelete(null)}
                style={{
                  background: "transparent", border: "1px solid #B8C5D4",
                  color: "#0F2A4A", padding: "10px 18px", borderRadius: 4,
                  fontWeight: 500, fontSize: 14, cursor: "pointer",
                }}>
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  background: "#B84237", border: "1px solid #B84237",
                  color: "#F4F6FA", padding: "10px 18px", borderRadius: 4,
                  fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <footer style={{ borderTop: "1px solid #D5DEE8", background: "#FFFFFF", padding: "20px 28px", marginTop: 60 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#4A5870" }}>
          <span className="mono" style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}>Submissions area — password protected.</span>
          <span className="mono" style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}>v2.0-US</span>
        </div>
      </footer>
    </div>
  );
}

// ============ FORM VIEW ============
function FormView({ form, setForm, onSubmit, submitting, justSubmitted }) {
  const update = (path, value) => {
    setForm(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  return (
    <>
      {justSubmitted && (
        <div style={{ marginBottom: 28, padding: "16px 20px", background: "#E8EFE8", border: "1px solid #2F5D3A", borderRadius: 6, display: "flex", alignItems: "center", gap: 12 }}>
          <Check size={18} color="#2F5D3A" />
          <div>
            <div style={{ fontWeight: 600, color: "#2F5D3A" }}>Submission saved</div>
            <div style={{ fontSize: 13, color: "#2F5D3A", marginTop: 2 }}>You can view it in the Submissions tab.</div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div style={{ marginBottom: 48 }}>
        <div className="subheading" style={{ marginBottom: 10 }}>Intake Form — Please complete in one sitting</div>
        <h1 className="display hero-headline" style={{ fontSize: 52, lineHeight: 1.02, fontWeight: 500, letterSpacing: "-0.03em", margin: 0 }}>
          Tell us how you <em style={{ fontStyle: "italic", color: "#4A90D9" }}>price</em> and <em style={{ fontStyle: "italic", color: "#4A90D9" }}>package</em> your work.
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: "#2A3649", marginTop: 20, maxWidth: 700 }}>
          The clearer your pricing, the better we can position your business in ads, on landing pages, and in sales calls. This usually takes 15–25 minutes.
        </p>
      </div>

      {/* Strategy callout */}
      <div className="section-card" style={{ background: "#0F2A4A", color: "#F4F6FA", border: "none" }}>
        <div className="subheading" style={{ color: "#D4B782", marginBottom: 14 }}>The Good / Better / Best Strategy</div>
        <h2 className="display" style={{ fontSize: 26, fontWeight: 500, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
          Why we recommend three tiers — and how to price them.
        </h2>
        <div className="strategy-cols" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, fontSize: 14, lineHeight: 1.6 }}>
          <div>
            <span className="level-pill" style={{ background: "#F4F6FA", color: "#0F2A4A", marginBottom: 10 }}>Level 1 — Good</span>
            <p style={{ margin: "10px 0 0", color: "#B8C5D4" }}>Entry-level coating + single-stage paint correction. The foot-in-the-door offer to get leads through the door.</p>
          </div>
          <div>
            <span className="level-pill" style={{ background: "#C9A86A", color: "#0F2A4A", marginBottom: 10 }}>Level 2 — Better</span>
            <p style={{ margin: "10px 0 0", color: "#B8C5D4" }}>Higher-grade / longer-warranty coating, same shape as L1. This should be your bestseller — price it <em>close</em> to L1.</p>
          </div>
          <div>
            <span className="level-pill" style={{ background: "#F4F6FA", color: "#0F2A4A", marginBottom: 10 }}>Level 3 — Best</span>
            <p style={{ margin: "10px 0 0", color: "#B8C5D4" }}>Everything you can do — flagship coating, two-stage correction, wheels off, full interior, engine bay.</p>
          </div>
        </div>
        <div style={{ marginTop: 24, padding: 16, background: "rgba(245,242,236,0.06)", borderLeft: "2px solid #D4B782", fontSize: 13, lineHeight: 1.6, color: "#E8F0FA" }}>
          <strong style={{ color: "#F4F6FA" }}>The goal:</strong> Level 1 is the <em>hook</em>. Our best clients use ads to get people in cheap, then upsell in person once the car is in the bay. Don't overprice L1 trying to protect margin — you'll make it back at the counter.
        </div>
      </div>

      {/* Business details */}
      <div className="section-card">
        <div className="subheading">Section 01</div>
        <div className="divider-heavy"></div>
        <h2 className="display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 20px", letterSpacing: "-0.02em" }}>Business Details</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          <div>
            <label className="label">Business Name *</label>
            <input className="field" value={form.business.name} onChange={e => update("business.name", e.target.value)} />
          </div>
          <div>
            <label className="label">Contact Name</label>
            <input className="field" value={form.business.contact} onChange={e => update("business.contact", e.target.value)} />
          </div>
          <div>
            <label className="label">Email</label>
            <input className="field" type="email" value={form.business.email} onChange={e => update("business.email", e.target.value)} />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="field" value={form.business.phone} onChange={e => update("business.phone", e.target.value)} />
          </div>
        </div>
      </div>

      {/* Levels */}
      <div className="section-card">
        <div className="subheading">Section 02</div>
        <div className="divider-heavy"></div>
        <h2 className="display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Good / Better / Best Packages</h2>
        <p style={{ color: "#4A5870", fontSize: 14, margin: "0 0 20px" }}>Describe each package and give a price per vehicle category.</p>

        <div style={{ marginBottom: 28, padding: 14, background: "#E8F0FA", borderLeft: "3px solid #C9A86A", borderRadius: "0 4px 4px 0", fontSize: 13, lineHeight: 1.5, color: "#2A3649" }}>
          <strong>Tip:</strong> include the <em>ceramic coating brand</em> (e.g. Gtechniq, Gyeon, CQuartz) in each package's description below. If you use different brands per level, that's fine — just mention the specific one you'd apply for that tier.
        </div>

        <LevelBlock label="Good" tier="good" color="#4A5870" form={form} update={update}
          hint="Entry-level ceramic coating + single-stage paint correction." />
        <div className="rule"></div>
        <LevelBlock label="Better" tier="better" color="#C9A86A" form={form} update={update}
          hint="Higher-grade coating, otherwise similar to Level 1. Your intended bestseller — price close to L1." isHighlighted />
        <div className="rule"></div>
        <LevelBlock label="Best" tier="best" color="#0F2A4A" form={form} update={update}
          hint="The full job. Top-tier coating, two-stage correction, wheels off, full interior, engine bay." />
      </div>

      {/* Add-ons */}
      <div className="section-card">
        <div className="subheading">Section 03</div>
        <div className="divider-heavy"></div>
        <h2 className="display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Extras & Add-Ons</h2>
        <p style={{ color: "#4A5870", fontSize: 14, margin: "0 0 24px" }}>
          Each extra has the simplest pricing shape for that service. Leave anything blank that you don't offer or that's already bundled.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {ADDONS.map(addon => (
            <AddonRow
              key={addon.name}
              addon={addon}
              value={form.addons[addon.name]}
              update={update}
            />
          ))}
        </div>
      </div>

      {/* Bonuses */}
      <div className="section-card">
        <div className="subheading">Section 04</div>
        <div className="divider-heavy"></div>
        <h2 className="display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Limited-Time Bonus Offer</h2>
        <p style={{ color: "#4A5870", fontSize: 14, margin: "0 0 20px" }}>
          We typically run a limited-time offer that throws in a high-perceived-value bonus for <em>free</em> with any package. Dramatically improves ad response rates.
        </p>

        <div style={{ display: "grid", gap: 20 }}>
          <ApprovalRow
            label="Option A — Windows/Glass + Wheel Face Coating, FREE with any package"
            value={form.bonusA}
            onChange={v => update("bonusA", v)}
          />
          <ApprovalRow
            label="Option B — Interior Refresh + Engine Bay Clean, FREE with any package"
            value={form.bonusB}
            onChange={v => update("bonusB", v)}
          />
        </div>

        <div style={{ marginTop: 28 }}>
          <label className="label">If you said No to both — what alternative bonus could you offer?</label>
          <textarea className="field" rows={3} placeholder="High perceived value, low delivery cost works best."
            value={form.bonusAlternative} onChange={e => update("bonusAlternative", e.target.value)} />
        </div>
      </div>

      {/* Additional services */}
      <div className="section-card">
        <div className="subheading">Section 05</div>
        <div className="divider-heavy"></div>
        <h2 className="display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Additional Automotive Services</h2>
        <p style={{ color: "#4A5870", fontSize: 14, margin: "0 0 28px" }}>We get asked about these constantly. Answer Yes or No — if yes, give us pricing.</p>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
            <h3 className="display" style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>Stone Chip Repair</h3>
            <div className="radio-group">
              <span className="label" style={{ margin: 0, alignSelf: "center" }}>Do you offer this?</span>
              <button className={`radio-opt ${form.stoneChip.offers === "yes" ? "active yes" : ""}`} onClick={() => update("stoneChip.offers", form.stoneChip.offers === "yes" ? "" : "yes")}>Yes</button>
              <button className={`radio-opt ${form.stoneChip.offers === "no" ? "active no" : ""}`} onClick={() => update("stoneChip.offers", form.stoneChip.offers === "no" ? "" : "no")}>No</button>
            </div>
          </div>
          {form.stoneChip.offers === "yes" && (
            <div>
              <label className="label">How do you price stone chip repairs, and what's your process?</label>
              <textarea className="field" rows={4}
                placeholder="e.g. How you price (per chip / per panel / flat rate), your typical process, and how you'd handle it alongside a ceramic coating booking."
                value={form.stoneChip.description}
                onChange={e => update("stoneChip.description", e.target.value)} />
              <div style={{ fontSize: 12, color: "#4A5870", marginTop: 8, fontStyle: "italic" }}>
                Include anything useful — your typical pricing approach, what you include vs. charge extra for, and how you'd combine it with a ceramic coating booking.
              </div>
            </div>
          )}
        </div>
        <div className="rule"></div>
        <OfferedServiceBlock
          title="Window Tinting"
          tierLabel="Package / Coverage"
          offered={form.tinting.offers}
          tiers={form.tinting.tiers}
          onOfferedChange={v => update("tinting.offers", v)}
          onTiersChange={tiers => update("tinting.tiers", tiers)}
          hintPlaceholder="e.g. Rear 3, Rear 5, Full car, Windshield strip, Ceramic upgrade"
          brand={form.tinting.brand}
          onBrandChange={v => update("tinting.brand", v)}
          brandLabel="Window Tint Film Brand(s) Used"
          brandPlaceholder="e.g. XPEL, 3M, Llumar, SunTek, Global Window Films"
        />
        <div className="rule"></div>
        <OfferedServiceBlock
          title="Paint Protection Film (PPF)"
          tierLabel="Package / Coverage"
          offered={form.ppf.offers}
          tiers={form.ppf.tiers}
          onOfferedChange={v => update("ppf.offers", v)}
          onTiersChange={tiers => update("ppf.tiers", tiers)}
          hintPlaceholder="e.g. Partial front, Full front, Track pack, Full vehicle, Film brand"
          brand={form.ppf.brand}
          onBrandChange={v => update("ppf.brand", v)}
          brandLabel="PPF Brand(s) Used"
          brandPlaceholder="e.g. XPEL, SunTek, STEK, 3M Scotchgard"
        />
        <div className="rule"></div>
        <div>
          <h3 className="display" style={{ fontSize: 20, fontWeight: 500, margin: "0 0 4px" }}>Other Services</h3>
          <p style={{ color: "#4A5870", fontSize: 13, margin: "0 0 16px" }}>Caliper painting, wheel refinishing, cosmetic repair — anything else you offer.</p>
          <TiersEditor
            tiers={form.other.tiers}
            onChange={tiers => update("other.tiers", tiers)}
            tierLabel="Service name"
            tierPlaceholder="e.g. Caliper painting (4), Wheel refinish (per wheel)"
          />
        </div>
      </div>

      {/* Approvals */}
      <div className="section-card">
        <div className="subheading">Section 06</div>
        <div className="divider-heavy"></div>
        <h2 className="display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 24px", letterSpacing: "-0.02em" }}>Final Approvals</h2>
        <div style={{ display: "grid", gap: 20 }}>
          <ApprovalRow
            label="I confirm the pricing above is accurate and can be used in ads, landing pages, and sales conversations."
            value={form.approvals.accurate}
            onChange={v => update("approvals.accurate", v)}
          />
        </div>
      </div>

      {/* Submit */}
      <div className="submit-bar" style={{ marginTop: 40, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
        <div style={{ fontSize: 13, color: "#4A5870", display: "flex", alignItems: "center", gap: 8 }}>
          <AlertCircle size={14} />
          <span>Your submission will be saved securely for our team to review.</span>
        </div>
        <button className="btn-primary submit-btn" onClick={onSubmit} disabled={submitting}>
          {submitting ? "Saving..." : "Submit Form"}
          {!submitting && <Check size={16} />}
        </button>
      </div>
    </>
  );
}

// ============ LEVEL BLOCK ============
function LevelBlock({ label, tier, color, form, update, hint, isHighlighted }) {
  const data = form.levels[tier];
  return (
    <div style={{
      padding: isHighlighted ? 20 : 0,
      background: isHighlighted ? "#E8F0FA" : "transparent",
      borderRadius: 4,
      margin: isHighlighted ? "0 -4px" : 0,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 6 }}>
        <span className="level-pill" style={{ background: color, color: color === "#C9A86A" ? "#0F2A4A" : "#F4F6FA" }}>Level — {label}</span>
        {isHighlighted && <span className="mono" style={{ fontSize: 10, color: "#A88840", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>★ Recommended Bestseller</span>}
      </div>
      <p style={{ color: "#4A5870", fontSize: 13, margin: "0 0 20px", fontStyle: "italic" }}>{hint}</p>

      <div className="stack-on-mobile" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 20 }}>
        <div>
          <label className="label">Coating Brand, Product + Correction</label>
          <textarea className="field" rows={2} placeholder="e.g. Gtechniq Crystal Serum Ultra, single-stage correction"
            value={data.description} onChange={e => update(`levels.${tier}.description`, e.target.value)} />
        </div>
        <div>
          <label className="label">Warranty</label>
          <input className="field" placeholder="e.g. 5-year"
            value={data.warranty} onChange={e => update(`levels.${tier}.warranty`, e.target.value)} />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label className="label">Included in this package</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 6 }}>

          {/* Paint correction — special row with its own options */}
          <div className="inclusion-row" style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            padding: "10px 12px",
            background: isHighlighted ? "#FFFFFF" : "#F4F6FA",
            border: "1px solid #D5DEE8",
            borderRadius: 4,
          }}>
            <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>Paint correction</span>
            <div className="radio-group" style={{ gap: 4, flexShrink: 0 }}>
              {[
                { key: "none", label: "None", icon: "✗" },
                { key: "single", label: "Single", icon: "1" },
                { key: "two-stage", label: "2-stage", icon: "2" },
                { key: "upgrade", label: "Upgrade", icon: "+" },
              ].map(opt => (
                <button key={opt.key}
                  className={`radio-opt ${data.paintCorrection === opt.key ? "active" : ""}`}
                  style={{ padding: "6px 9px", fontSize: 10 }}
                  onClick={() => update(`levels.${tier}.paintCorrection`, data.paintCorrection === opt.key ? "" : opt.key)}>
                  {opt.icon}
                  <span style={{ marginLeft: 3 }}>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Standard inclusions */}
          {INCLUSIONS.map(inc => (
            <div key={inc} className="inclusion-row" style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: "10px 12px",
              background: isHighlighted ? "#FFFFFF" : "#F4F6FA",
              border: "1px solid #D5DEE8",
              borderRadius: 4,
            }}>
              <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>{inc}</span>
              <div className="radio-group" style={{ gap: 4, flexShrink: 0 }}>
                {["yes", "no", "upgrade"].map(opt => (
                  <button key={opt}
                    className={`radio-opt ${data.inclusions[inc] === opt ? "active" : ""}`}
                    style={{ padding: "6px 9px", fontSize: 10 }}
                    onClick={() => update(`levels.${tier}.inclusions.${inc}`, opt)}>
                    {opt === "yes" ? "✓" : opt === "no" ? "✗" : "+"}
                    <span style={{ marginLeft: 3 }}>{opt}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="label">Price by Vehicle ($)</label>
        <div className="level-prices-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
          {VEHICLES.map(v => (
            <div key={v}>
              <div className="mono" style={{ fontSize: 10, color: "#4A5870", marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>{v}</div>
              <input className="field field-sm" placeholder="$" style={{ textAlign: "center" }}
                value={data.prices[v]} onChange={e => update(`levels.${tier}.prices.${v}`, e.target.value)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ ADDON ROW ============
// Renders an addon's input UI based on its `type`.
// per_vehicle → horizontal row of inputs for each vehicle category
// flat        → single price input (optionally with an extra field, e.g. warranty)
// per_unit    → single price input with a unit label (e.g. "$ per seat")
function AddonRow({ addon, value, update }) {
  return (
    <div style={{
      padding: 16,
      background: "#FFFFFF",
      border: "1px solid #D5DEE8",
      borderRadius: 4,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: addon.note ? 4 : 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: "#0F2A4A" }}>{addon.name}</div>
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "#C9A86A" }}>
          {addon.type === "per_vehicle" && "Price per vehicle"}
          {addon.type === "flat" && "Flat price"}
          {addon.type === "per_unit" && `Price ${addon.unitLabel}`}
        </div>
      </div>
      {addon.note && (
        <div style={{ fontSize: 12, color: "#4A5870", fontStyle: "italic", marginBottom: 12 }}>{addon.note}</div>
      )}

      {addon.type === "per_vehicle" && (
        <div className="per-vehicle-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
          {VEHICLES.map(v => (
            <div key={v}>
              <div className="mono" style={{ fontSize: 10, color: "#4A5870", marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>{v}</div>
              <input className="field field-sm" placeholder="$" style={{ textAlign: "center" }}
                value={value?.prices?.[v] || ""}
                onChange={e => update(`addons.${addon.name}.prices.${v}`, e.target.value)} />
            </div>
          ))}
        </div>
      )}

      {addon.type === "flat" && (
        <div style={{ display: "grid", gridTemplateColumns: addon.extraField ? "200px 1fr" : "200px", gap: 14 }}>
          <div>
            <label className="label" style={{ margin: "0 0 4px" }}>Price ($)</label>
            <input className="field field-sm" placeholder="$"
              value={value?.price || ""}
              onChange={e => update(`addons.${addon.name}.price`, e.target.value)} />
          </div>
          {addon.extraField && (
            <div>
              <label className="label" style={{ margin: "0 0 4px" }}>{addon.extraField.label}</label>
              <input className="field field-sm" placeholder={addon.extraField.placeholder || ""}
                value={value?.[addon.extraField.key] || ""}
                onChange={e => update(`addons.${addon.name}.${addon.extraField.key}`, e.target.value)} />
            </div>
          )}
        </div>
      )}

      {addon.type === "per_unit" && (
        <div style={{ width: 200 }}>
          <label className="label" style={{ margin: "0 0 4px" }}>Price {addon.unitLabel} ($)</label>
          <input className="field field-sm" placeholder="$"
            value={value?.price || ""}
            onChange={e => update(`addons.${addon.name}.price`, e.target.value)} />
        </div>
      )}
    </div>
  );
}

// ============ APPROVAL ROW ============
function ApprovalRow({ label, value, onChange }) {
  return (
    <div className="approval-row" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 20, alignItems: "center", padding: "12px 16px", background: "#F4F6FA", borderRadius: 4 }}>
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>{label}</div>
      <div className="radio-group">
        <button className={`radio-opt ${value === "yes" ? "active yes" : ""}`} onClick={() => onChange(value === "yes" ? "" : "yes")}>
          <Check size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 3 }} />Approve
        </button>
        <button className={`radio-opt ${value === "no" ? "active no" : ""}`} onClick={() => onChange(value === "no" ? "" : "no")}>
          <X size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 3 }} />Decline
        </button>
      </div>
    </div>
  );
}

// ============ OFFERED SERVICE ============
function OfferedServiceBlock({ title, tierLabel, offered, tiers, onOfferedChange, onTiersChange, hintPlaceholder, brand, onBrandChange, brandLabel, brandPlaceholder }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <h3 className="display" style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>{title}</h3>
        <div className="radio-group">
          <span className="label" style={{ margin: 0, alignSelf: "center" }}>Do you offer this?</span>
          <button className={`radio-opt ${offered === "yes" ? "active yes" : ""}`} onClick={() => onOfferedChange(offered === "yes" ? "" : "yes")}>Yes</button>
          <button className={`radio-opt ${offered === "no" ? "active no" : ""}`} onClick={() => onOfferedChange(offered === "no" ? "" : "no")}>No</button>
        </div>
      </div>
      {offered === "yes" && (
        <>
          {onBrandChange && (
            <div style={{ marginBottom: 14, padding: 14, background: "#E8F0FA", borderLeft: "3px solid #C9A86A", borderRadius: "0 4px 4px 0" }}>
              <label className="label">{brandLabel || "Brand(s) Used"}</label>
              <input className="field" placeholder={brandPlaceholder || ""}
                value={brand} onChange={e => onBrandChange(e.target.value)} />
            </div>
          )}
          <TiersEditor tiers={tiers} onChange={onTiersChange} tierLabel={tierLabel} tierPlaceholder={hintPlaceholder} />
        </>
      )}
    </div>
  );
}

// ============ TIERS EDITOR ============
function TiersEditor({ tiers, onChange, tierLabel, tierPlaceholder }) {
  const update = (idx, field, value) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };
  const add = () => onChange([...tiers, blankPricingTier()]);
  const remove = idx => onChange(tiers.filter((_, i) => i !== idx));
  return (
    <div>
      <div className="tiers-header" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr auto", gap: 10, paddingBottom: 8, borderBottom: "1px solid #B8C5D4", marginBottom: 8 }}>
        <div className="pricing-grid-header" style={{ textAlign: "left" }}>{tierLabel}</div>
        <div className="pricing-grid-header" style={{ textAlign: "left" }}>Price ($)</div>
        <div className="pricing-grid-header" style={{ textAlign: "left" }}>Notes</div>
        <div></div>
      </div>
      {tiers.map((t, i) => (
        <div key={i} className="tiers-row" style={{ display: "grid", gridTemplateColumns: "2fr 1fr 2fr auto", gap: 10, marginBottom: 8, alignItems: "center" }}>
          <div>
            <div className="mobile-only-label" style={{ display: "none" }}>{tierLabel}</div>
            <input className="field field-sm" placeholder={tierPlaceholder || ""} value={t.tier} onChange={e => update(i, "tier", e.target.value)} />
          </div>
          <div>
            <div className="mobile-only-label" style={{ display: "none" }}>Price ($)</div>
            <input className="field field-sm" placeholder="$" value={t.price} onChange={e => update(i, "price", e.target.value)} />
          </div>
          <div>
            <div className="mobile-only-label" style={{ display: "none" }}>Notes</div>
            <input className="field field-sm" placeholder="Notes" value={t.notes} onChange={e => update(i, "notes", e.target.value)} />
          </div>
          <button className="btn-ghost" style={{ padding: "8px 12px" }} onClick={() => remove(i)} disabled={tiers.length === 1}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
      <button className="btn-ghost" style={{ marginTop: 4 }} onClick={add}>
        <Plus size={12} />Add row
      </button>
    </div>
  );
}

// ============ PASSWORD GATE ============
function PasswordGate({ pwInput, setPwInput, pwError, onSubmit }) {
  return (
    <div style={{ maxWidth: 440, margin: "80px auto", padding: "48px 40px", background: "#FFFFFF", border: "1px solid #D5DEE8", borderRadius: 6, textAlign: "center" }}>
      <div style={{ width: 56, height: 56, margin: "0 auto 20px", borderRadius: "50%", background: "#0F2A4A", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Lock size={22} color="#F4F6FA" />
      </div>
      <div className="subheading" style={{ marginBottom: 10 }}>Restricted Area</div>
      <h2 className="display" style={{ fontSize: 28, fontWeight: 500, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
        Enter <em style={{ color: "#4A90D9", fontStyle: "italic" }}>password</em>
      </h2>
      <p style={{ color: "#4A5870", fontSize: 14, margin: "0 0 28px", lineHeight: 1.5 }}>
        Submissions are protected. Please enter the access password to continue.
      </p>
      <input
        className="field"
        type="password"
        autoFocus
        placeholder="Password"
        value={pwInput}
        onChange={e => setPwInput(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") onSubmit(); }}
        style={{ textAlign: "center", fontSize: 15, letterSpacing: "0.1em", marginBottom: 14 }}
      />
      {pwError && (
        <div style={{ fontSize: 13, color: "#B84237", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          <AlertCircle size={14} /> Incorrect password. Please try again.
        </div>
      )}
      <button className="btn-primary" onClick={onSubmit} style={{ width: "100%", justifyContent: "center" }}>
        Unlock <Check size={16} />
      </button>
    </div>
  );
}

// ============ SUBMISSIONS VIEW ============
function SubmissionsView({ submissions, loading, openSub, setOpenSub, onDelete, onExport, onLock }) {
  if (loading) {
    return <div style={{ textAlign: "center", padding: 80, color: "#4A5870" }}>Loading submissions…</div>;
  }

  return (
    <>
      <div style={{ marginBottom: 32, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 20, flexWrap: "wrap" }}>
        <div>
          <div className="subheading" style={{ marginBottom: 10 }}>Admin view — Password protected</div>
          <h1 className="display page-headline" style={{ fontSize: 52, lineHeight: 1.02, fontWeight: 500, letterSpacing: "-0.03em", margin: 0 }}>
            Submissions <span style={{ color: "#4A90D9", fontStyle: "italic" }}>({submissions.length})</span>
          </h1>
        </div>
        <button className="btn-ghost" onClick={onLock}>
          <Lock size={12} /> Lock
        </button>
      </div>

      {submissions.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", background: "#FFFFFF", border: "1px dashed #B8C5D4", borderRadius: 6 }}>
          <FileText size={36} color="#9AA8B8" style={{ marginBottom: 12 }} />
          <div className="display" style={{ fontSize: 22, fontWeight: 500, marginBottom: 6 }}>No submissions yet</div>
          <div style={{ color: "#4A5870", fontSize: 14 }}>Once a client submits the form, it'll appear here.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {submissions.map(sub => (
            <SubmissionCard
              key={sub.id}
              sub={sub}
              isOpen={openSub === sub.id}
              onToggle={() => setOpenSub(openSub === sub.id ? null : sub.id)}
              onDelete={() => onDelete(sub.id)}
              onExport={() => onExport(sub)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function SubmissionCard({ sub, isOpen, onToggle, onDelete, onExport }) {
  const d = sub.data;
  const date = new Date(d.submittedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = new Date(d.submittedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="sub-card">
      <div className="sub-header" onClick={onToggle}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {isOpen ? <ChevronDown size={18} color="#4A5870" /> : <ChevronRight size={18} color="#4A5870" />}
          <div>
            <div className="display" style={{ fontSize: 19, fontWeight: 500 }}>{d.business.name || "(Unnamed business)"}</div>
            <div className="mono" style={{ fontSize: 11, color: "#4A5870", letterSpacing: "0.06em", marginTop: 3 }}>
              {date} · {time} {d.business.contact && `· ${d.business.contact}`}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }} onClick={e => e.stopPropagation()}>
          <button className="btn-ghost" onClick={onExport} title="Export as JSON">
            <Download size={12} />
          </button>
          <button className="btn-ghost" onClick={onDelete} title="Delete" style={{ color: "#B84237" }}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {isOpen && <SubmissionDetail data={d} />}
    </div>
  );
}

function SubmissionDetail({ data }) {
  const Row = ({ label, value }) => (
    <div className="info-row">
      <div className="info-label">{label}</div>
      <div className={value ? "info-value" : "info-empty"}>{value || "—"}</div>
    </div>
  );

  const renderLevel = (tierKey, tierLabel) => {
    const lvl = data.levels?.[tierKey];
    if (!lvl) return null;
    // Paint correction display
    const pcLabels = { none: "None", single: "Single-stage", "two-stage": "Two-stage", upgrade: "Upgrade option" };
    const pcValue = lvl.paintCorrection ? (pcLabels[lvl.paintCorrection] || lvl.paintCorrection) : null;
    // Inclusions — combine paint correction + standard items; also handle legacy "Paint correction" and "Ceramic paint coating" keys
    const inclusionItems = [];
    if (pcValue) inclusionItems.push(`Paint correction (${pcValue})`);
    // Legacy: old submissions may still have these in inclusions
    if (lvl.inclusions?.["Ceramic paint coating"] && lvl.inclusions["Ceramic paint coating"] !== "no") {
      inclusionItems.push(`Ceramic paint coating (${lvl.inclusions["Ceramic paint coating"]})`);
    }
    if (!pcValue && lvl.inclusions?.["Paint correction"] && lvl.inclusions["Paint correction"] !== "no") {
      inclusionItems.push(`Paint correction (${lvl.inclusions["Paint correction"]})`);
    }
    INCLUSIONS.filter(i => lvl.inclusions?.[i] && lvl.inclusions[i] !== "no").forEach(i => inclusionItems.push(`${i} (${lvl.inclusions[i]})`));
    return (
      <div style={{ marginBottom: 20 }}>
        <div className="subheading" style={{ marginBottom: 8 }}>Level — {tierLabel}</div>
        <Row label="Description" value={lvl.description} />
        <Row label="Warranty" value={lvl.warranty} />
        <Row label="Prices" value={VEHICLES.map(v => `${v}: $${lvl.prices?.[v] || "—"}`).join("  ·  ")} />
        <Row label="Inclusions" value={inclusionItems.join("  ·  ") || "none marked"} />
      </div>
    );
  };

  const renderService = (key, label) => {
    const svc = data[key];
    if (!svc) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <Row label={label} value={
          svc.offers === "yes"
            ? "Yes — " + (svc.tiers || []).filter(t => t.tier || t.price).map(t => `${t.tier}${t.price ? ` $${t.price}` : ""}${t.notes ? ` (${t.notes})` : ""}`).join("  ·  ")
            : svc.offers === "no" ? "No" : "—"
        } />
        {svc.offers === "yes" && svc.brand && (
          <Row label={`${label} Brand(s)`} value={svc.brand} />
        )}
      </div>
    );
  };

  return (
    <div className="sub-body">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 32, marginBottom: 28 }}>
        <div>
          <div className="subheading" style={{ marginBottom: 8 }}>Business</div>
          <Row label="Name" value={data.business?.name} />
          <Row label="Contact" value={data.business?.contact} />
          <Row label="Email" value={data.business?.email} />
          <Row label="Phone" value={data.business?.phone} />
        </div>
        <div>
          <div className="subheading" style={{ marginBottom: 8 }}>Approvals & Bonuses</div>
          <Row label="Pricing Accurate" value={data.approvals?.accurate === "yes" ? "✓ Approved" : data.approvals?.accurate === "no" ? "✗ Declined" : "—"} />
          {/* Legacy approvals — only shown if present from earlier submissions */}
          {data.approvals?.uplift && (
            <Row label="5% Uplift (legacy)" value={data.approvals.uplift === "yes" ? "✓ Approved" : "✗ Declined"} />
          )}
          {data.approvals?.strategy && (
            <Row label="G/B/B Strategy (legacy)" value={data.approvals.strategy === "yes" ? "✓ Approved" : "✗ Declined"} />
          )}
          <Row label="Bonus A (Glass/Wheel)" value={data.bonusA === "yes" ? "✓ Approved" : data.bonusA === "no" ? "✗ Declined" : "—"} />
          <Row label="Bonus B (Interior+Engine)" value={data.bonusB === "yes" ? "✓ Approved" : data.bonusB === "no" ? "✗ Declined" : "—"} />
          <Row label="Alt. bonus" value={data.bonusAlternative} />
        </div>
      </div>

      <div style={{ borderTop: "1px solid #B8C5D4", paddingTop: 20, marginBottom: 20 }}>
        <div className="subheading" style={{ marginBottom: 12 }}>Packages</div>
        {data.ceramicBrand && <Row label="Ceramic Brand(s)" value={data.ceramicBrand} />}
        {renderLevel("good", "Good")}
        {renderLevel("better", "Better")}
        {renderLevel("best", "Best")}
      </div>

      <div style={{ borderTop: "1px solid #B8C5D4", paddingTop: 20 }}>
        <div className="subheading" style={{ marginBottom: 12 }}>Add-ons</div>
        {ADDONS.map(addon => {
          const v = data.addons?.[addon.name];
          if (!v) return null;

          // New per_vehicle shape: { prices: { City Car: ... } }
          // Legacy shape: { City Car: ... } (top-level vehicle keys)
          if (addon.type === "per_vehicle") {
            const prices = v.prices || v; // fall back to legacy shape
            const entries = VEHICLES.filter(veh => prices?.[veh]).map(veh => `${veh}: ${prices[veh]}`);
            if (entries.length === 0) return null;
            return <Row key={addon.name} label={addon.name} value={entries.join("  ·  ")} />;
          }

          if (addon.type === "flat") {
            if (!v.price && !v[addon.extraField?.key]) return null;
            const parts = [];
            if (v.price) parts.push(`$${v.price}`);
            if (addon.extraField && v[addon.extraField.key]) parts.push(`${addon.extraField.label}: ${v[addon.extraField.key]}`);
            return <Row key={addon.name} label={addon.name} value={parts.join("  ·  ")} />;
          }

          if (addon.type === "per_unit") {
            if (!v.price) return null;
            return <Row key={addon.name} label={addon.name} value={`$${v.price} ${addon.unitLabel}`} />;
          }

          return null;
        })}
      </div>

      <div style={{ borderTop: "1px solid #B8C5D4", paddingTop: 20, marginTop: 20 }}>
        <div className="subheading" style={{ marginBottom: 12 }}>Additional Services</div>
        {/* Stone chip: new shape uses {offers, description}; legacy shape uses {offers, tiers} */}
        {(() => {
          const sc = data.stoneChip;
          if (!sc) return null;
          if (sc.offers === "yes" && sc.description) {
            return (
              <div style={{ marginBottom: 14 }}>
                <Row label="Stone Chip" value="Yes — see process below" />
                <div style={{ padding: "10px 0 6px 16px", fontSize: 13, color: "#2A3649", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                  {sc.description}
                </div>
              </div>
            );
          }
          if (sc.offers === "yes" && sc.tiers) {
            // legacy fallback
            return (
              <Row label="Stone Chip" value={"Yes — " + sc.tiers.filter(t => t.tier || t.price).map(t => `${t.tier}${t.price ? ` $${t.price}` : ""}`).join("  ·  ")} />
            );
          }
          return <Row label="Stone Chip" value={sc.offers === "no" ? "No" : "—"} />;
        })()}
        {renderService("tinting", "Window Tinting")}
        {renderService("ppf", "PPF")}
        {data.other?.tiers?.filter(t => t.tier || t.price).length > 0 && (
          <Row label="Other Services" value={data.other.tiers.filter(t => t.tier || t.price).map(t => `${t.tier}${t.price ? ` $${t.price}` : ""}`).join("  ·  ")} />
        )}
      </div>
    </div>
  );
}
